import { useEffect, useState } from 'react';
import { faX } from '@fortawesome/pro-regular-svg-icons/faX';
import { Image } from 'rebass';
import { FormattedMessage } from 'react-intl';
import { Box } from '@osmosis/react-ui/components/Box';
import { Button } from '@osmosis/react-ui/components/Button';
import { Flex } from '@osmosis/react-ui/components/Box';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import { randomIntFromInterval } from '@osmosis/shared/number';
import {
  completedMessages,
  MIN_ITEMS_FOR_JOYSNACKBAR,
  themes,
  quotes,
} from '../../constants';
import { H2 } from '@osmosis/react-ui/components/Text';
import { P } from '@osmosis/react-ui/components/Text';
import { useQuizContext } from '../../context/QuizContext';
import useModal from '../../hooks/useModal';
import { AnimatePresence } from 'motion/react';
import * as motion from 'motion/react-m';
import { slideVerticalReverse } from '../../styles/animation';

const JoySnackbarQuote = ({
  quote,
  quoteAuthor,
}: {
  quote: string;
  quoteAuthor: string;
}) => {
  return (
    <Flex flexDirection="column">
      <Box
        sx={{
          position: 'relative',
          bg: 'rgba(255, 255, 255, 0.2)',
          p: '10px 15px',
          color: 'textInvert',
          borderRadius: '10px',
          fontSize: '16px',
          lineHeight: 1.5,
        }}
      >
        <Box
          as="span"
          sx={{
            color: 'textInvert',
            position: 'absolute',
            top: '-3px',
            left: '-12px',
            fontSize: '50px',
          }}
        >
          <FormattedMessage
            id="quiz.joysnackbar.left-quote"
            defaultMessage="“"
          />
        </Box>
        <P sx={{ color: 'textInvert', fontSize: '16px' }}>{quote}</P>
        <Box
          as="span"
          sx={{
            color: 'textInvert',
            position: 'absolute',
            right: '-12px',
            bottom: '-32px',
            fontSize: '50px',
          }}
        >
          <FormattedMessage
            id="quiz.joysnackbar.right-quote"
            defaultMessage="”"
          />
        </Box>
      </Box>
      <P
        sx={{
          fontSize: '14px',
          color: 'textInvert',
          textAlign: 'right',
          fontStyle: 'italic',
        }}
      >
        {quoteAuthor}
      </P>
    </Flex>
  );
};

type SnackbarData = {
  type: 'quote' | 'message';
  themeIndex: number;
  quoteIndex: number;
  messageIndex: number;
};

/**
 * Https://elsevier.atlassian.net/browse/OSMS-2889 .
 */
const JoySnackbar = () => {
  const { quiz, totalItems, currentIndex } = useQuizContext();

  const joySnackbar = useModal();
  const [snackbarData, setSnackbarData] = useState<SnackbarData>();
  const itemResponses = quiz.filter((i) => i.response).length;
  const correctAnswers = quiz.filter((i) => i.response?.accuracy).length;
  const colors = useColors();
  const leftCount = totalItems - (currentIndex + 1);

  useEffect(() => {
    if (
      totalItems >= MIN_ITEMS_FOR_JOYSNACKBAR &&
      itemResponses === Math.ceil(totalItems / 2) &&
      correctAnswers >= 2 &&
      !joySnackbar.isVisible &&
      !snackbarData
    ) {
      joySnackbar.show();

      const type = Math.floor(Math.random() * 8) > 1 ? 'quote' : 'message';
      const themeIndex = randomIntFromInterval(0, themes.length - 1);
      const quoteIndex = randomIntFromInterval(0, quotes.length - 1);
      const messageIndex = randomIntFromInterval(
        0,
        completedMessages.length - 1,
      );

      setSnackbarData({
        type,
        themeIndex,
        quoteIndex,
        messageIndex,
      });
    }
  }, [itemResponses, snackbarData, joySnackbar, totalItems]);

  const handleClose = () => {
    joySnackbar.hide();
  };

  if (!snackbarData) return null;

  const bgColor = colors[themes[snackbarData.themeIndex].theme];
  const img = themes[snackbarData.themeIndex].src;
  const quote = quotes[snackbarData.quoteIndex].quote;
  const quoteAuthor = quotes[snackbarData.quoteIndex].author;

  return (
    <AnimatePresence>
      {joySnackbar.isVisible && (
        <motion.div
          {...slideVerticalReverse()}
          style={{
            position: 'fixed',
            right: 30,
            bottom: 30,
            zIndex: 9999,
          }}
        >
          <Flex
            sx={{
              flexDirection: 'column',
              width: 300,
              p: 4,
              borderRadius: 5,
              boxShadow: 'large',
              gap: 3,
              backgroundColor: bgColor,
            }}
          >
            {snackbarData.type === 'quote' ? (
              <JoySnackbarQuote quote={quote} quoteAuthor={quoteAuthor} />
            ) : (
              <H2 textAlign="center" color="textInvert">
                {completedMessages[snackbarData.messageIndex]}
              </H2>
            )}

            <Box width="100%" px={3}>
              <Image sx={{ width: '100%' }} src={img} alt="Joy Snackbar Img" />
            </Box>

            <P color="textInvert">
              <FormattedMessage
                id="quiz.joysnackbar.greet-work"
                defaultMessage="Great work so far, <b>{count} more questions</b> to go, you got this!"
                values={{
                  count: leftCount,
                  b: (chunks) => <strong key="b">{chunks}</strong>,
                }}
              />
            </P>
            <Button
              startIcon={<Icon icon={faX} fontSize="12px" />}
              variant="outline"
              onClick={handleClose}
              data-testid="close-joysnackbar"
              color="textInvert"
            >
              <FormattedMessage id="common.close" defaultMessage="Close" />
            </Button>
          </Flex>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default JoySnackbar;
